import DocsLayout, { CodeBlock, Callout } from '../../components/docs/DocsLayout';

export default function DocsMultiSig() {
  return (
    <DocsLayout 
      title="Multi-Sig" 
      description="Gnosis Safe integration for enhanced security"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          For high-value operations or shared control, AIBL supports Gnosis Safe multi-signature wallets. This adds human-in-the-loop approval for transactions, ensuring multiple parties must sign off before execution.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">When to Use Multi-Sig</h2>
        
        <ul className="space-y-2 text-white/70">
          <li>• High-value agent wallets requiring additional security</li>
          <li>• Team-managed agents where multiple approvals are needed</li>
          <li>• Compliance requirements for institutional deployments</li>
          <li>• Treasury management with multiple stakeholders</li>
        </ul>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Safe Operations</h2>

        <div className="space-y-4">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">Deploy Safe</h3>
            <p className="text-sm text-white/60">
              Create a new Gnosis Safe with your chosen signers and threshold. The agent's Turnkey signer is automatically included.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">Import Safe</h3>
            <p className="text-sm text-white/60">
              Connect an existing Gnosis Safe to your agent. The agent signer must already be added as an owner.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">Discover Safes</h3>
            <p className="text-sm text-white/60">
              Automatically find Safes where your agent's signer is already an owner across supported chains.
            </p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Signers and Threshold</h2>
        
        <p className="text-white/70 mb-4">
          A Gnosis Safe is defined by its signers (owners) and threshold (required signatures):
        </p>

        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <p className="text-center text-white/70 mb-4">Example: 2-of-3 Safe</p>
          <div className="flex justify-center gap-4">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mb-2">
                <span className="text-green-400">✓</span>
              </div>
              <span className="text-xs text-white/50">Signer 1</span>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mb-2">
                <span className="text-green-400">✓</span>
              </div>
              <span className="text-xs text-white/50">Signer 2</span>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-2">
                <span className="text-white/30">—</span>
              </div>
              <span className="text-xs text-white/50">Signer 3</span>
            </div>
          </div>
          <p className="text-center text-sm text-white/50 mt-4">2 signatures required to execute</p>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Transaction Flow</h2>

        <ol className="space-y-4 text-white/70">
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-sm font-bold text-blue-400">1</span>
            <div>
              <strong className="text-white">Propose</strong>
              <p>A signer creates a transaction proposal. It enters the pending queue.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-sm font-bold text-blue-400">2</span>
            <div>
              <strong className="text-white">Approve/Reject</strong>
              <p>Other signers review and add their signature or reject the proposal.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-sm font-bold text-blue-400">3</span>
            <div>
              <strong className="text-white">Execute</strong>
              <p>Once threshold is met, any signer can execute the transaction on-chain.</p>
            </div>
          </li>
        </ol>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Shareable Sign Page</h2>
        
        <p className="text-white/70 mb-4">
          External co-signers can approve transactions without logging into your AIBL account. Share the dedicated sign URL:
        </p>

        <CodeBlock language="text">
{`https://app.aibl.dev/dashboard/safesign?safe=0x...&chain=base`}
        </CodeBlock>

        <Callout type="info" title="Co-Signer Access">
          Co-signers only need to connect their wallet to approve transactions. They don't need an AIBL account or access to your dashboard.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">API Reference</h2>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Deploy Safe</h3>
        <CodeBlock language="bash">
{`POST /v1/safe/deploy
{
  "agentId": "agent_123",
  "signers": ["0x...", "0x..."],
  "threshold": 2
}`}
        </CodeBlock>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Get Pending Transactions</h3>
        <CodeBlock language="bash">
{`GET /v1/safe/{agentId}/transactions?status=pending`}
        </CodeBlock>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Approve Transaction</h3>
        <CodeBlock language="bash">
{`POST /v1/safe/confirm/{safeTxId}
{
  "signature": "0x..."
}`}
        </CodeBlock>
      </section>
    </DocsLayout>
  );
}
